import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  getAllClients,
  getChecklistByClientId,
  getClientAccesses,
  getClientTasks,
  getAllTasks,
  getClientStats,
  getTaskStats,
  premiumOnboardingChecklist
} from '@/data/mocks/administration';
import { useRestaurants } from '@/hooks/useRestaurants';
import {
  Users,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Clock,
  DollarSign,
  FileText,
  Key,
  ListTodo,
  Calendar,
  Target,
  Mail,
  Phone,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Filter,
  Download,
  Plus,
  FileCheck
} from 'lucide-react';
import { Client, ChecklistItem, ClientAccess, AdminTask } from '@/types';
import { QuoteGeneratorV2, CGVDocument, ContractExpress, AccessRequirementsForm, PremiumPackPresentation, NDAManager } from '@/components/documents';

export const AdministrationPage: React.FC = () => {
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [expandedChecklist, setExpandedChecklist] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  
  // Utiliser les vraies données Supabase pour les clients
  const { data: restaurantsData } = useRestaurants();
  const clients = getAllClients(); // Pour les autres onglets (temporaire)
  
  // Calculer les stats depuis les vraies données
  const clientStats = restaurantsData ? {
    total: restaurantsData.length,
    active: restaurantsData.filter(r => r.status === 'active').length,
    totalRevenue: restaurantsData.reduce((sum, r) => sum + (r.monthly_revenue || 0), 0),
  } : { total: 0, active: 0, totalRevenue: 0 };
  
  const taskStats = getTaskStats();
  const allTasks = getAllTasks();

  const toggleChecklistItem = (itemId: string) => {
    setExpandedChecklist(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'default';
      case 'pending': return 'warning';
      case 'cancelled': return 'danger';
      case 'active': return 'success';
      case 'negociation': return 'warning';
      case 'received': return 'success';
      case 'requested': return 'warning';
      case 'configured': return 'success';
      case 'issue': return 'danger';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'commercial': return DollarSign;
      case 'legal': return FileText;
      case 'technical': return Key;
      case 'operational': return ListTodo;
      default: return CheckCircle2;
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      commercial: 'Commercial',
      legal: 'Légal',
      technical: 'Technique',
      operational: 'Opérationnel',
      seo: 'SEO',
      content: 'Contenu',
      reporting: 'Reporting',
      communication: 'Communication'
    };
    return labels[category] || category;
  };

  const filteredChecklist = filterCategory === 'all' 
    ? premiumOnboardingChecklist 
    : premiumOnboardingChecklist.filter(item => item.category === filterCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto space-y-8"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Administration
            </h1>
            <p className="text-gray-600">
              Gestion des clients et suivi des projets
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Client
            </Button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-8 h-8 text-blue-600" />
                <Badge variant="default">Total</Badge>
              </div>
              <p className="text-3xl font-bold text-blue-900 mb-1">
                {clientStats.total}
              </p>
              <p className="text-sm text-blue-700">
                Clients ({clientStats.active} actifs)
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-green-600" />
                <Badge variant="success">MRR</Badge>
              </div>
              <p className="text-3xl font-bold text-green-900 mb-1">
                {clientStats.totalRevenue.toLocaleString()}€
              </p>
              <p className="text-sm text-green-700">
                Revenu mensuel récurrent
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
              <div className="flex items-center justify-between mb-4">
                <Clock className="w-8 h-8 text-yellow-600" />
                <Badge variant="warning">En cours</Badge>
              </div>
              <p className="text-3xl font-bold text-yellow-900 mb-1">
                {taskStats.inProgress + taskStats.pending}
              </p>
              <p className="text-sm text-yellow-700">
                Tâches à faire
              </p>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <Target className="w-8 h-8 text-purple-600" />
                <Badge variant="default">Objectif</Badge>
              </div>
              <p className="text-3xl font-bold text-purple-900 mb-1">
                {taskStats.completionRate}%
              </p>
              <p className="text-sm text-purple-700">
                Taux de complétion
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="clients" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto">
            <TabsTrigger value="clients">
              <Users className="w-4 h-4 mr-2" />
              Clients
            </TabsTrigger>
            <TabsTrigger value="checklist">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Checklist
            </TabsTrigger>
            <TabsTrigger value="tasks">
              <ListTodo className="w-4 h-4 mr-2" />
              Tâches
            </TabsTrigger>
            <TabsTrigger value="accesses">
              <Key className="w-4 h-4 mr-2" />
              Accès
            </TabsTrigger>
            <TabsTrigger value="documents">
              <FileCheck className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
          </TabsList>

          {/* CLIENTS TAB */}
          <TabsContent value="clients" className="space-y-6">
            {restaurantsData?.map((restaurant) => {
              const client = {
                id: restaurant.id,
                restaurantName: restaurant.name,
                status: restaurant.status,
                pack: restaurant.pack || 'essentiel',
                monthlyRevenue: restaurant.monthly_revenue || 0,
                primaryContact: {
                  name: (restaurant.primary_contact as any)?.name || 'Non renseigné',
                  email: restaurant.email || 'Non renseigné',
                  phone: restaurant.phone || 'Non renseigné',
                  role: (restaurant.primary_contact as any)?.role || 'Non renseigné',
                },
                onboarding: {
                  completionRate: 0, // À implémenter plus tard
                },
                billing: {
                  method: (restaurant.billing_info as any)?.method || 'Non renseigné',
                  frequency: (restaurant.billing_info as any)?.frequency || 'monthly',
                },
                notes: '',
              };
              return (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="p-6">
                  <div className="space-y-6">
                    {/* Client Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-900">
                            {client.restaurantName}
                          </h3>
                          <Badge variant={getStatusColor(client.status as any)}>
                            {client.status === 'active' ? 'Actif' : client.status === 'prospect' ? 'Prospect' : client.status === 'paused' ? 'En pause' : client.status}
                          </Badge>
                          <Badge variant="outline">
                            Pack {client.pack.charAt(0).toUpperCase() + client.pack.slice(1)}
                          </Badge>
                        </div>
                        <p className="text-gray-600 mb-4">
                          {client.monthlyRevenue.toLocaleString()}€/mois
                        </p>
                      </div>
                      <Button
                        variant={selectedClient === client.id ? 'default' : 'outline'}
                        onClick={() => setSelectedClient(
                          selectedClient === client.id ? null : client.id
                        )}
                      >
                        {selectedClient === client.id ? 'Masquer détails' : 'Voir détails'}
                      </Button>
                    </div>

                    {/* Onboarding Progress */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700 font-medium">
                          Progression onboarding
                        </span>
                        <span className="text-gray-900 font-bold">
                          {client.onboarding.completionRate}%
                        </span>
                      </div>
                      <Progress value={client.onboarding.completionRate} />
                    </div>

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Contact principal</p>
                        <p className="font-medium text-gray-900">{client.primaryContact.name}</p>
                        <p className="text-sm text-gray-600">{client.primaryContact.role}</p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <a href={`mailto:${client.primaryContact.email}`} className="text-blue-600 hover:underline">
                            {client.primaryContact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a href={`tel:${client.primaryContact.phone}`} className="text-blue-600 hover:underline">
                            {client.primaryContact.phone}
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {client.notes && (
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm font-medium text-yellow-900 mb-1">Notes</p>
                        <p className="text-sm text-yellow-800">{client.notes}</p>
                      </div>
                    )}

                    {/* Detailed View */}
                    {selectedClient === client.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pt-6 border-t space-y-6"
                      >
                        {/* Tasks for this client */}
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-4">
                            Tâches en cours
                          </h4>
                          <div className="space-y-3">
                            {getClientTasks(client.id).map((task) => (
                              <div
                                key={task.id}
                                className="p-4 bg-white border rounded-lg hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <h5 className="font-medium text-gray-900 mb-1">
                                      {task.title}
                                    </h5>
                                    <p className="text-sm text-gray-600">
                                      {task.description}
                                    </p>
                                  </div>
                                  <Badge variant={getPriorityColor(task.priority)}>
                                    {task.priority}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  <div className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {new Date(task.dueDate).toLocaleDateString('fr-FR')}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    {task.estimatedHours}h estimées
                                  </div>
                                  <Badge variant="outline">
                                    {getCategoryLabel(task.category)}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Accesses for this client */}
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-4">
                            Accès demandés
                          </h4>
                          <div className="space-y-2">
                            {getClientAccesses(client.id).map((access) => (
                              <div
                                key={access.id}
                                className="flex items-center justify-between p-3 bg-white border rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <Key className="w-5 h-5 text-gray-400" />
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {access.service}
                                    </p>
                                    {access.url && (
                                      <a
                                        href={access.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                      >
                                        Accéder
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    )}
                                  </div>
                                </div>
                                <Badge variant={getStatusColor(access.status)}>
                                  {access.status === 'requested' ? 'Demandé' : access.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </Card>
              </motion.div>
            );
            })}
            
            {/* Empty state */}
            {(!restaurantsData || restaurantsData.length === 0) && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Aucun client
                </h3>
                <p className="text-gray-600">
                  Les clients ajoutés apparaîtront ici.
                </p>
              </div>
            )}
          </TabsContent>

          {/* CHECKLIST TAB */}
          <TabsContent value="checklist" className="space-y-6">
            {/* Filter */}
            <Card className="p-4">
              <div className="flex items-center gap-4">
                <Filter className="w-5 h-5 text-gray-400" />
                <div className="flex gap-2 flex-wrap">
                  {['all', 'commercial', 'legal', 'technical', 'operational'].map((cat) => (
                    <Button
                      key={cat}
                      variant={filterCategory === cat ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterCategory(cat)}
                    >
                      {cat === 'all' ? 'Tout' : getCategoryLabel(cat)}
                    </Button>
                  ))}
                </div>
              </div>
            </Card>

            {/* Checklist Items */}
            <div className="space-y-3">
              {filteredChecklist.map((item) => {
                const Icon = getCategoryIcon(item.category);
                const isExpanded = expandedChecklist.includes(item.id);
                const completedSubtasks = item.subtasks?.filter(s => s.completed).length || 0;
                const totalSubtasks = item.subtasks?.length || 0;
                const progress = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <Card className="p-6 hover:shadow-lg transition-shadow">
                      <div
                        className="flex items-start gap-4 cursor-pointer"
                        onClick={() => toggleChecklistItem(item.id)}
                      >
                        <div className={`p-3 rounded-lg ${
                          item.category === 'commercial' ? 'bg-blue-100' :
                          item.category === 'legal' ? 'bg-purple-100' :
                          item.category === 'technical' ? 'bg-green-100' :
                          'bg-yellow-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${
                            item.category === 'commercial' ? 'text-blue-600' :
                            item.category === 'legal' ? 'text-purple-600' :
                            item.category === 'technical' ? 'text-green-600' :
                            'text-yellow-600'
                          }`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 mb-1">
                                {item.title}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {item.description}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={getPriorityColor(item.priority)}>
                                {item.priority === 'high' ? 'Haute' : 
                                 item.priority === 'medium' ? 'Moyenne' : 'Basse'}
                              </Badge>
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Progress bar */}
                          {totalSubtasks > 0 && (
                            <div className="space-y-2 mt-4">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">
                                  {completedSubtasks}/{totalSubtasks} sous-tâches
                                </span>
                                <span className="font-medium text-gray-900">
                                  {Math.round(progress)}%
                                </span>
                              </div>
                              <Progress value={progress} />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Subtasks */}
                      {isExpanded && item.subtasks && item.subtasks.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-6 ml-20 space-y-2"
                        >
                          {item.subtasks.map((subtask) => (
                            <div
                              key={subtask.id}
                              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={subtask.completed}
                                onChange={() => {}}
                                className="w-5 h-5 text-stratos-gold rounded focus:ring-stratos-gold"
                              />
                              <span className={`text-sm ${
                                subtask.completed ? 'text-gray-400 line-through' : 'text-gray-900'
                              }`}>
                                {subtask.title}
                              </span>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          {/* TASKS TAB */}
          <TabsContent value="tasks" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {allTasks.map((task) => {
                const client = clients.find(c => c.id === task.clientId);
                return (
                  <motion.div
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="p-6 hover:shadow-lg transition-shadow h-full">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-lg font-bold text-gray-900">
                              {task.title}
                            </h3>
                            <Badge variant={getPriorityColor(task.priority)}>
                              {task.priority}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {task.description}
                          </p>
                          {client && (
                            <p className="text-sm font-medium text-blue-600 mb-3">
                              Client : {client.restaurantName}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Échéance : {new Date(task.dueDate).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span>{task.estimatedHours}h</span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline">
                            {getCategoryLabel(task.category)}
                          </Badge>
                          {task.tags.map((tag) => (
                            <Badge key={tag} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>

                        {task.recurring && (
                          <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <p className="text-sm text-purple-900">
                              🔁 Récurrent : {task.recurring.frequency === 'monthly' ? 'Mensuel' : task.recurring.frequency}
                            </p>
                          </div>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>

          {/* ACCESSES TAB */}
          <TabsContent value="accesses" className="space-y-6">
            {clients.map((client) => {
              const accesses = getClientAccesses(client.id);
              return (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {client.restaurantName}
                    </h3>
                    <div className="space-y-3">
                      {accesses.map((access) => (
                        <div
                          key={access.id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <Key className="w-6 h-6 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {access.service}
                              </p>
                              {access.url && (
                                <a
                                  href={access.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  {access.url}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              {access.notes && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {access.notes}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Demandé le {new Date(access.requestedDate).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          <Badge variant={getStatusColor(access.status)}>
                            {access.status === 'requested' ? 'Demandé' :
                             access.status === 'received' ? 'Reçu' :
                             access.status === 'configured' ? 'Configuré' :
                             access.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-6">
            <Tabs defaultValue="quote" className="space-y-6">
              <TabsList className="grid w-full grid-cols-6 lg:w-auto">
                <TabsTrigger value="quote">Devis</TabsTrigger>
                <TabsTrigger value="contract">Contrat Express</TabsTrigger>
                <TabsTrigger value="premium">Pack Premium</TabsTrigger>
                <TabsTrigger value="cgv">CGPS</TabsTrigger>
                <TabsTrigger value="nda">NDA</TabsTrigger>
                <TabsTrigger value="access">Besoins d'Accès</TabsTrigger>
              </TabsList>

              <TabsContent value="quote">
                <QuoteGeneratorV2 />
              </TabsContent>

              <TabsContent value="contract">
                <ContractExpress />
              </TabsContent>

              <TabsContent value="premium">
                <PremiumPackPresentation />
              </TabsContent>

              <TabsContent value="cgv">
                <CGVDocument />
              </TabsContent>

              <TabsContent value="nda">
                <NDAManager />
              </TabsContent>

              <TabsContent value="access">
                <AccessRequirementsForm />
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
};

